import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Search, X } from 'lucide-react';

/**
 * ClientSearch — la ricerca usa SOLO il "nickname" (nome con cui conosciamo il cliente).
 * La ragione sociale (name) e altri dati appaiono solo nei dettagli sotto.
 * Se il cliente non ha nickname, la ricerca fa fallback su name.
 */
const ClientSearch = ({ clients, value, onChange, placeholder = "Cerca cliente per nome (come lo conosci)...", disabled = false }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = clients.find(c => c.id === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Nome che mostriamo come "principale": nickname se presente, altrimenti name
  const displayName = (c) => (c.nickname && c.nickname.trim()) ? c.nickname : c.name;

  const filtered = search.length > 0
    ? clients.filter(c => displayName(c).toLowerCase().includes(search.toLowerCase()))
        .slice(0, 25)
    : clients.slice(0, 25);

  const handlePick = (client) => {
    onChange(client.id);
    setSearch('');
    setIsOpen(false);
  };

  const clear = () => {
    onChange('');
    setSearch('');
  };

  return (
    <div ref={wrapperRef} className={`relative ${disabled ? 'pointer-events-none opacity-70' : ''}`}>
      {selected && !isOpen ? (
        <div
          className="flex items-center justify-between gap-2 px-3 py-2 bg-muted/30 border border-border rounded-sm cursor-pointer hover:bg-muted/50"
          onClick={() => !disabled && setIsOpen(true)}
          data-testid="client-search-selected"
        >
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold truncate">{displayName(selected)}</p>
            {selected.nickname && selected.name && selected.nickname !== selected.name && (
              <p className="text-xs text-muted-foreground truncate">
                <span className="font-medium">{selected.name}</span>
                {selected.piva && ` · P.IVA ${selected.piva}`}
              </p>
            )}
            <p className="text-xs text-muted-foreground truncate">
              {selected.address && `${selected.address} · `}{selected.cap} {selected.city}
              {!selected.nickname && selected.piva && ` · P.IVA ${selected.piva}`}
            </p>
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }} className="text-muted-foreground hover:text-destructive">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="client-search-input"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder}
            className="h-10 pl-10"
            autoFocus={isOpen}
          />
        </div>
      )}

      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-sm shadow-lg max-h-80 overflow-auto">
          {filtered.length > 0 ? (
            filtered.map(client => (
              <div
                key={client.id}
                onClick={() => handlePick(client)}
                data-testid={`client-search-option-${client.id}`}
                className="px-3 py-2 hover:bg-muted cursor-pointer border-b border-border/50 last:border-0"
              >
                <p className="text-sm font-semibold">{displayName(client)}</p>
                {client.nickname && client.name && client.nickname !== client.name && (
                  <p className="text-[11px] text-muted-foreground italic truncate">{client.name}</p>
                )}
                <p className="text-xs text-muted-foreground truncate">
                  {client.city} {client.cap}{client.piva && ` · P.IVA ${client.piva}`}
                </p>
              </div>
            ))
          ) : (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">
              Nessun cliente trovato
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientSearch;
